"use client"

import type React from "react"
import Link from "next/link"
import { useState, useEffect } from "react"
import { Sun, Moon } from "lucide-react"

interface MainNavProps extends React.HTMLAttributes<HTMLElement> {
  className?: string
}

export function MainNav({ className, ...props }: MainNavProps) {
  const [isDarkMode, setIsDarkMode] = useState(false)

  // 로컬 스토리지에서 다크 모드 상태 확인
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme")
    if (savedTheme === "dark") {
      setIsDarkMode(true)
      document.documentElement.classList.add("dark")
    } else {
      document.documentElement.classList.remove("dark")
    }
  }, [])

  // 다크 모드 변경 함수
  const toggleTheme = () => {
    setIsDarkMode((prev) => {
      const newTheme = !prev
      if (newTheme) {
        localStorage.setItem("theme", "dark")
        document.documentElement.classList.add("dark")
      } else {
        localStorage.setItem("theme", "light")
        document.documentElement.classList.remove("dark")
      }
      return newTheme
    })
  }

  const routes = [
    { href: "/", label: "홈" },
    { href: "/games", label: "게임" },
    { href: "/bbs", label: "커뮤니티" },
    { href: "/ranking", label: "랭킹" },
    { href: "/account", label: "회원/로그인" },
  ]

  return (
    <div className={`
        ${className} 
        container 
        mx-auto 
        flex 
        items-center 
        justify-center 
        md:justify-between 
        py-2 
        px-6
      `}
    >
      {/* 로고: 모바일에서는 텍스트를 숨기고 아이콘만 보여주며 중앙 정렬 */}
      <div className="flex items-center justify-between w-full">
        <Link href="/" className="flex items-center space-x-2 max-[768px]:mx-auto">
          <img
            src="/logo.png"
            width={32}
            height={32}
            alt="플래시게임 로고"
            className="inline-block"
          />
          <span className="hidden md:inline text-xl font-black">
            플래시게임
          </span>
        </Link>
        {/* 데스크톱 메뉴 & 다크 모드 토글: 모바일에서는 숨김 */}
        <nav className="flex items-center space-x-9 max-[768px]:hidden" {...props}>
          {routes.map((route) => (
            <Link
              key={route.href}
              href={route.href}
              className="
                text-sm
                font-medium 
                transition-colors
                hover:text-blue-600
              ">
              {route.label}
            </Link>
          ))}
        </nav>
        <div className="max-[768px]:hidden">
          <button
            onClick={toggleTheme}
            className="
            bg-white
            dark:bg-white
            text-black
              p-2 
              bg-opacity-80 
              backdrop-blur-md 
              border 
              border-gray-300 
              rounded-full 
              transition-colors 
              dark:hover:bg-stone-300
            "
            aria-label="다크 모드 토글"
          >
            {isDarkMode ? (
              <Sun size={20} />
            ) : (
              <Moon size={20} />
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
